import cx from 'classnames';
import {isEmpty, map, split} from 'lodash';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, DataWrapper, Divider, Dropdown, Icon, SessionTextChat, Toggle} from 'components';
import {Language} from 'constants/language';
import {UserContext, UserSessionContext} from 'context';
import {AudioStreamState, SttClientState} from 'enums';
import {useUserSession} from 'hooks';
import {SessionActivityName, WebRTCSpeechActivityData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

import {TranslationSettings} from '../../TranslationSettings';
import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.TRANSCRIPTIONS_SIDEBAR;

export const TranscriptionsSidebar: React.FC = () => {
    const {t} = useTranslation();
    const [aiSuggestionsModalOpen, setAiSuggestionsModalOpen] = useState(false);
    const [aiSuggestionHistoryModalOpen, setAiSuggestionHistoryModalOpen] = useState(false);

    const {
        session, audioActivity, canManageSession, audioVideoActivity, aiSuggestionsAudioEnabled,
        isAudioTranslationEnabled, isAudioVideoTranslationEnabled, audioStreamEnabled
    } = useUserSession();

    const {
        aiSuggestionsAudio,
        aiSuggestionsAudioError,
        aiSuggestionsAudioLoading,
        subtitlesThreads,
        requestAiSuggestionsAudio,
        updateTranscriptionSettings,
        onAudioStreamingToggle,
        audioStreamState
    } = useContext(UserSessionContext);

    const {socket} = useContext(UserContext);

    const firstSuggestionText = useMemo(() => aiSuggestionsAudio?.[0]?.text, [aiSuggestionsAudio]);

    const activity = useMemo(() => audioVideoActivity || audioActivity, [audioActivity, audioVideoActivity]);

    const activityData = useMemo(() => activity?.data as WebRTCSpeechActivityData, [activity?.data]);

    const isStreamingEnabled = useMemo(() => activityData?.streamAudio?.enabled, [activityData]);

    const streamingStatus = useMemo(() => {
        if (!isStreamingEnabled) {
            return {label: T_KEYS.STATUS.OFF, color: 'tw-text-default-dimmed'};
        }
        if (!socket?.connected || audioStreamState === AudioStreamState.Closed || audioStreamState === SttClientState.Failed) {
            return {label: T_KEYS.STATUS.DISCONNECTED, color: 'tw-text-alert'};
        }
        if (!audioStreamEnabled) {
            return {label: T_KEYS.STATUS.PAUSED, color: 'tw-text-default-dimmed'};
        }
        if (audioStreamState === AudioStreamState.Active) {
            return {label: T_KEYS.STATUS.CONNECTED, color: 'tw-text-success'};
        }
        return {label: T_KEYS.STATUS.CONNECTING, color: 'tw-text-warning'};
    }, [isStreamingEnabled, socket?.connected, audioStreamState, audioStreamEnabled]);

    const handleStreamingToggle = useCallback((value: boolean) => {
        if (activity) {
            onAudioStreamingToggle(activity.id, value);
        }
    }, [activity, onAudioStreamingToggle]);

    const requestAiSuggestionsFn = useCallback(() => {
        if (activity) {
            requestAiSuggestionsAudio();
            setAiSuggestionsModalOpen(true);
        }
    }, [activity, requestAiSuggestionsAudio]);

    const formattedSuggestions = useMemo(() => map(split(firstSuggestionText, '\n'), (text, i) =>
        <div key={i} className={text ? '' : 'tw-mb-2'}>{text}</div>
    ), [firstSuggestionText]);

    return (
        <SidebarContent view={SidebarView.Transcriptions} header={<div className='tw-ts-h5'>{t(T_KEYS.TITLE)}</div>}>
            <div className='tw-flex tw-flex-col tw-h-full'>
                <div className='tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-2'>
                    <Toggle
                        checked={isStreamingEnabled}
                        name='stream-toggle'
                        disabled={!canManageSession}
                        label={t(T_KEYS.TOGGLE.LABEL)}
                        onChange={handleStreamingToggle}
                    />
                    <span
                        className={cx(
                            'tw-border tw-rounded-full tw-px-2 tw-py-0.5 tw-text-sm tw-whitespace-nowrap',
                            streamingStatus.color
                        )}
                        style={{borderColor: 'currentColor'}}
                        role='status'
                        title={t(T_KEYS.STATUS.LABEL)}
                    >
                        {t(streamingStatus.label)}
                    </span>
                </div>

                {aiSuggestionsAudioEnabled && canManageSession &&
                    <Fragment>
                        <div className='tw-flex tw-items-center'>
                            <Dropdown
                                open={aiSuggestionsModalOpen}
                                triggerNode={
                                    <Button
                                        icon='chatbot'
                                        onClick={requestAiSuggestionsFn}
                                    >
                                        {t(T_KEYS.AI_SUGGESTIONS.LABEL)}
                                    </Button>
                                }
                                childrenClassName='tw-p-2'
                                onClose={() => setAiSuggestionsModalOpen(false)}
                            >
                                <div className='tw-w-64 tw-relative'>
                                    <DataWrapper
                                        isLoading={aiSuggestionsAudioLoading}
                                        isError={aiSuggestionsAudioError}
                                        svgClassName='tw-max-h-8'
                                        errorClassName='tw-text-base'
                                        errorPlaceholder={t(T_KEYS.AI_SUGGESTIONS.ERROR)}
                                    >
                                        <div className='tw-ts-base tw-text-left'>
                                            {firstSuggestionText ? formattedSuggestions : t(T_KEYS.AI_SUGGESTIONS.NO_DATA)}
                                        </div>
                                    </DataWrapper>
                                </div>
                            </Dropdown>

                            {!isEmpty(firstSuggestionText) &&
                                <Dropdown
                                    open={aiSuggestionHistoryModalOpen}
                                    triggerNode={<div className='tw-ml-4'><Icon name='text-chat-history' button /></div>}
                                    childrenClassName='tw-p-2'
                                    onOpen={() => setAiSuggestionHistoryModalOpen(true)}
                                    onClose={() => setAiSuggestionHistoryModalOpen(false)}
                                >
                                    <div className='tw-w-64 tw-relative tw-ts-base tw-text-left'>
                                        {formattedSuggestions}
                                    </div>
                                </Dropdown>}
                        </div>
                        <Divider className='tw-my-4' />
                    </Fragment>}

                {(activity?.name === SessionActivityName.Audio && isAudioTranslationEnabled ||
                    activity?.name === SessionActivityName.AudioVideo && isAudioVideoTranslationEnabled) &&
                        <Fragment>
                            <TranslationSettings
                                session={session}
                                includeLanguages={[Language.English, Language.Spanish, Language.French, Language.Portuguese]}
                                settings={activityData?.translationSettings}
                                disabled={!canManageSession}
                                onChange={(data) => updateTranscriptionSettings(activity.id, data)}
                            />
                            <Divider className='tw-my-4' />
                        </Fragment>}

                <div className='transcription-messages tw-overflow-auto'>
                    <SessionTextChat
                        session={session}
                        threads={subtitlesThreads}
                        activityName={activity?.name}
                        disableNewMessageSound
                    />
                </div>
            </div>

        </SidebarContent>
    );
};
